import type { IndustrialCaseDocument } from './industrialCaseDocument';

/**
 * ### IndustrialCaseSummary
 *
 * What a card needs to represent a case: enough to decide whether to open it,
 * and nothing more.
 *
 * Cards appear in the case list and in the related cases at the end of a
 * detail page, both of which render many cases at once. Passing whole documents
 * would ship every body section and reference to the browser for cases the
 * reader may never open, so the server narrows them to this first.
 */
export type IndustrialCaseSummary = {
  readonly slug: string;
  readonly solution: IndustrialCaseDocument['solution'];
  readonly title: string;
  readonly summary: string;
  /** Desktop hero image, reused as the card thumbnail */
  readonly heroImage: string;
  readonly customerName: string;
  readonly industry: string;
  readonly process: string;
  readonly inspectionTarget: string;
  readonly targetSound: string;
  readonly primaryMetric: {
    readonly value: string;
    readonly label: string;
  };
  readonly publishedAt: string;
};

/**
 * ### toIndustrialCaseSummary
 *
 * Narrows a published case to what a card shows.
 *
 * @param document A published case document
 * @returns The card-sized view of it
 */
export function toIndustrialCaseSummary(
  document: IndustrialCaseDocument,
): IndustrialCaseSummary {
  return {
    slug: document.slug,
    solution: document.solution,
    title: document.title,
    summary: document.summary,
    heroImage: document.heroImage.pc,
    customerName: document.customerName,
    industry: document.industry,
    process: document.process,
    inspectionTarget: document.inspectionTarget,
    targetSound: document.targetSound,
    primaryMetric: {
      value: document.primaryMetric.value,
      label: document.primaryMetric.label,
    },
    publishedAt: document.publishedAt,
  };
}

/**
 * ### describeIndustrialCaseClassification
 *
 * Joins industry, process, and inspection target into the one line a card and
 * the detail hero both show, skipping the parts a case has not recorded.
 *
 * @param caseFields The classification fields as stored
 * @returns The filled classification values, in reading order
 */
export function describeIndustrialCaseClassification(caseFields: {
  industry: string;
  process: string;
  inspectionTarget: string;
}): Array<string> {
  return [
    caseFields.industry,
    caseFields.process,
    caseFields.inspectionTarget,
  ]
    .map((value) => value.trim())
    .filter((value) => value !== '');
}
